import React, { useEffect, useRef, useCallback } from 'react';

// Google's own Identity Services script — no wrapper npm package needed.
// https://developers.google.com/identity/gsi/web/guides/overview
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let scriptLoadingPromise = null;
function loadGoogleScript() {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptLoadingPromise;
}

// Renders Google's own "Sign in with Google" button. `onCredential` is
// called with the ID token string, which the caller sends to
// POST /auth/google for verification — this component never talks to our
// backend itself.
const GoogleSignInButton = ({ onCredential, onError, text = 'signin_with' }) => {
  const buttonRef = useRef(null);

  const handleCredentialResponse = useCallback((response) => {
    if (response?.credential) {
      onCredential(response.credential);
    } else if (onError) {
      onError(new Error('No credential returned from Google'));
    }
  }, [onCredential, onError]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !buttonRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadGoogleScript()
      .then(() => {
        if (cancelled || !buttonRef.current) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse
        });

        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text
        });
      })
      .catch((err) => {
        console.error('Failed to load Google Identity Services:', err);
        if (onError) onError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [handleCredentialResponse, onError, text]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return <div ref={buttonRef} />;
};

export default GoogleSignInButton;
